import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { Dispatch } from 'redux';
import { GIButton } from 'app-components';
import { FormattedMessage, useIntl } from 'react-intl';

import { DATE_FORMAT, formatDateToMMDDYYYY } from '../../../utils/dates/dateMethods';
import { useAppSelector, useAppDispatch } from '../../../hooks';
import { useAttachmentList, useIssuerList } from '../hooks/useAdminSerff';
import {
    fetchSerffTransferStatusDataList,
} from '../../../actions/admin/serff/serffTransferStatusActionCreators';
import { setAdminSerffIssuersOptions } from '../../../actions/admin/serff/serffPlansLoadStatusActionCreators';
import ReactDatepicker from '../../../lib/inputs/react-datepicker';
import { ConditionalRendering } from '../../../components/common/conditional-rendering';
import PaginatedTable, { renderCellContent } from '../../../components/common/table/PaginatedTable';
import AdminSerffModal, { HandleModalContent } from './AdminSerffModal';
import Dropdown from '../../../lib/inputs/Dropdown';
import { IFormData } from '../types/adminSerffStatus.interface';
import { INPUT_TYPES } from '../../../components/common/form/constants/fieldTypes';
import { ADMIN_SERFF_PLAN_LOAD_STATUS_ACTION_TYPES } from '../../../constants/actionTypes/adminSerffStatusActionTypes';
import { getTransferStatusFilters, TRANSFER_STATUS_MAPPING } from '../constants/enum';

const StyledContainer = styled.div`
    margin: 20px 0px;

    .tag-dropdown-label {
        padding-right: 5rem;
        padding-left: 0;
    }
`;

const StyledGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    grid-template-rows: auto;
    gap: 16px 8px;
    & > div:nth-child(n + 4) {
        grid-column: span 1;
    }
    padding: 2rem 1rem;
`;

const StyledButton = styled.div`
    align-self: end;
    
    button {
        margin-left: 1rem;
    }
`;

const StyledField = styled.div`
    gap: 20px;

    > div {
        display: flex;
        flex-direction: column;
        align-items: start;
        margin: 0;
    }

    .gi-input {
        text-align: left;
    }
    .gi-input__label {
        padding: 0;
    }

    input,
    select {
        width: 80%;
    }

    .react-datepicker-wrapper {
        width: 80%;
    }

    .react-datepicker__input-container {
        input {
            width: 100%;
        }
    }
`;

const StyledLabel = styled.label`
    cursor: pointer;
    color: ${({ theme }) => theme.primaryMain};
`;

const AdminSerffTransferStatusContainer = () => {
    const intl = useIntl();
    const statusOptions = React.useMemo(() => getTransferStatusFilters(intl), [intl]);
    const resetValues = {
        issuerFilter: '',
        statusFilter: '',
        dateFilter: '',
    };

    const dispatch: Dispatch<any> = useAppDispatch();
    const { issuerDropdownOptions } = useIssuerList(intl, 'admin.serff.transferStatus.dropdown.issuer.placeholder');
    const {
        serffTransferStatusData,
    } = useAppSelector(state => state.adminPortal.adminSerffTransferStatus);
    const {
        filters,
    } = useAppSelector(state => state.adminPortal.adminSerffIsserList);

    const {
        content = [],
        pageable: { pageNumber = 1, pageSize = 10 } = {},
        totalPages = 0,
        totalElements = 0,
        first = false,
    } = serffTransferStatusData;
    
    const {
        issuers = [],
    } = filters;

    const { fetchAttachments } = useAttachmentList();

    const [currentPage, setCurrentPage] = useState(pageNumber);
    const [currentPageSize, setPageSize] = useState(pageSize);
    const [formData, setFormData] = useState<IFormData>(resetValues);
    const [showModal, setShowModal] = useState(false);
    const [modalData, setModalData] = useState({
        title: 'admin.serff.transferStatus.modal.serffRequest',
        content: '',
        type: 'serffRequest',
    });
   
    useEffect(() => {
        dispatch(setAdminSerffIssuersOptions(issuerDropdownOptions));
    }, [dispatch, issuerDropdownOptions]);

    useEffect(() => {
        dispatch(fetchSerffTransferStatusDataList({...resetValues, page: currentPage, size: currentPageSize}));
    }, []);

    const handleDropdownChange = (fieldKey: string, value: string) => {
        setFormData((prevFormData) => ({
            ...prevFormData,
            [fieldKey]: value || '',
        }));
    };
      
    const onUpdateDateFields = (value: Date | null) => {
        const formattedDate = formatDateToMMDDYYYY(value);
        setFormData((prevFormData) => ({
            ...prevFormData,
            dateFilter: formattedDate,
        }));
    };

    const handleSubmit = (event) => {
        setCurrentPage(0);
        event.preventDefault();
        dispatch({
            type: ADMIN_SERFF_PLAN_LOAD_STATUS_ACTION_TYPES.SET_FILTERS,
            payload: {
                issuerFilter: formData.issuerFilter,
                statusFilter: formData.statusFilter,
                dateFilter: formData.dateFilter,
            },
        });
    
        dispatch(fetchSerffTransferStatusDataList({
            issuerFilter: formData.issuerFilter,
            statusFilter: formData.statusFilter,
            dateFilter: formData.dateFilter,
            page: 0,
            size: currentPageSize
        }));
    };

    const handleReset = () => {
        setFormData(resetValues);
    
        dispatch({
            type: ADMIN_SERFF_PLAN_LOAD_STATUS_ACTION_TYPES.SET_FILTERS,
            payload: resetValues,
        });
    
        dispatch(fetchSerffTransferStatusDataList({...resetValues,page: currentPage, size: currentPageSize}));
    };

    const handlePageChange = page => {
        setCurrentPage(page);
        dispatch(
            fetchSerffTransferStatusDataList({
                issuerFilter: formData.issuerFilter,
                statusFilter: formData.statusFilter,
                dateFilter: formData.dateFilter,
                size: currentPageSize,
                page,
            }),
        );
    };

    const handlePageSizeChange = size => {
        setPageSize(size);
        dispatch(
            fetchSerffTransferStatusDataList({
                issuerFilter: formData.issuerFilter,
                statusFilter: formData.statusFilter,
                dateFilter: formData.dateFilter,
                page: currentPage,
                size,
            }),
        );
    };

    const handleShowModal = (key, content) => {
        const modalTitles = {
            'serffResponse': 'admin.serff.transferStatus.modal.serffResponse',
            'serffRequest': 'admin.serff.transferStatus.modal.serffRequest',
            'serffAttachments': 'admin.serff.transferStatus.modal.attachmentsDetail',
        };

        setModalData(()=>({title: modalTitles[key], content: content, type: key}));
        setShowModal(!showModal);
    };

    const handleModalContent = (content) => {
        setModalData((prev)=>({...prev, content: content}));
    };

    const handleAttachmentClick = async (selectedRecordID: string | number) => {
        const data = await fetchAttachments(selectedRecordID);
        handleShowModal('serffAttachments', data);
    };  

    const dropdownConfigs = [
        {
            id: 'issuerFilter',
            labelId: 'admin.serff.plansLoadStatus.dropdown.issuer',
            options: issuers,
            value: formData.issuerFilter,
        },
        {
            id: 'statusFilter',
            labelId: 'admin.serff.plansLoadStatus.dropdown.status',
            options: statusOptions,
            value: formData.statusFilter,
        },
    ];

    const columns = [
        {
            key: 'serffReqID',
            label: <FormattedMessage id='admin.serff.transferStatus.columns.serffReqID'/>,
            render: (item: any) => renderCellContent(item?.serffReqID,{width:'120px'}),
        },
        {
            key: 'hiosIssuerID',
            label: <FormattedMessage id='admin.serff.transferStatus.columns.hiosIssuerID'/>,
            render: (item: any) => renderCellContent(item?.hiosIssuerID),
        },
        {
            key: 'planNumber',
            label: <FormattedMessage id='admin.serff.transferStatus.columns.planNumber'/>,
            render: (item: any) => renderCellContent(item?.planNumber,{width:'200px'}),
        },
        {
            key: 'serffTrackNumber',
            label: <FormattedMessage id='admin.serff.transferStatus.columns.serffTrackNumber'/>,
            render: (item: any) => renderCellContent(item?.serffTrackNumber),
        },
        {
            key: 'stateTrackNumber',
            label: <FormattedMessage id='admin.serff.transferStatus.columns.stateTrackNumber'/>,
            render: (item: any) => renderCellContent(item?.stateTrackNumber),
        },
        {
            key: 'serffRequest',
            label: <FormattedMessage id='admin.serff.transferStatus.columns.serffRequest'/>,
            render: (item: any) => <StyledLabel onClick={() => handleShowModal('serffRequest', item?.serffRequest || '')}>{renderCellContent('Serff Request',{width:'100px'})}</StyledLabel>,
            filterOptions: ['HIDE_COLUMN']
        },
        {
            key: 'serffResponse',
            label: <FormattedMessage id='admin.serff.transferStatus.columns.serffResponse'/>,
            render: (item: any) => <StyledLabel onClick={() => handleShowModal('serffResponse',item?.serffResponse || '')}>{renderCellContent('Serff Response',{width:'100px'})}</StyledLabel>,
            filterOptions: ['HIDE_COLUMN']
        },
        {
            key: 'startDate',
            label: <FormattedMessage id='admin.serff.transferStatus.columns.startDate'/>,
            render: (item: any) => renderCellContent(item?.startDate),
        },
        {
            key: 'endDate',
            label: <FormattedMessage id='admin.serff.transferStatus.columns.endDate'/>,
            render: (item: any) => renderCellContent(item?.endDate),
        },
        {
            key: 'status',
            label: <FormattedMessage id='admin.serff.transferStatus.columns.status'/>,
            render: (item: any) => renderCellContent(TRANSFER_STATUS_MAPPING[item?.status],{width:'100px'}),
            filterOptions: ['HIDE_COLUMN']
        },
        {
            key: 'statusDescription',
            label: <FormattedMessage id='admin.serff.transferStatus.columns.statusDescription'/>,
            render: (item: any) => renderCellContent(item?.statusDescription,{width:'180px'}),
        },
        {
            key: 'serffReqID-attachments',
            label: <FormattedMessage id='admin.serff.transferStatus.columns.serffReqID-attachments'/>,
            render: (item: any) => <StyledLabel onClick={() => handleAttachmentClick(item?.serffReqID)}>{renderCellContent('attachments',{width:'120px'})}</StyledLabel>,
            filterOptions: ['HIDE_COLUMN']
        },
    ];

    return (
        <StyledContainer data-testid="admin-serff-transfer-status-container">
            <FormattedMessage
                id={'admin.serff.transferStatus'}
                tagName="h1"
                data-testid="serff-transfer-status-title"
            />
            <form
                onSubmit={e => handleSubmit(e)}
                className="form-group"
                data-testid="serff-transfer-status-form"
            >
                <StyledGrid data-testid="form-grid">
                    {dropdownConfigs?.map(({ id, labelId, options, value }) => (
                        <StyledField key={id} data-testid={`dropdown-wrapper-${id}`}>
                            <Dropdown
                                label={{
                                    text: <FormattedMessage id={labelId} />,
                                    labelClass: ['tag-dropdown-label'],
                                }}
                                inputData={{
                                    options,
                                    type: INPUT_TYPES.DROPDOWN,
                                    value,
                                    isInputRequired: false,
                                }}
                                inputActions={{
                                    handleFieldChange: value => handleDropdownChange(id, value),
                                }}
                                data-testid={`dropdown-${id}`}
                            />
                        </StyledField>
                    ))}

                    <StyledField data-testid="datepicker-wrapper-dateFilter">
                        <ReactDatepicker
                            labelText="common.date"
                            wrapperClasses={['date-picker']}
                            id="adminSerffPlansLoadStatusdDateUI"
                            type="text"
                            pattern={DATE_FORMAT}
                            disabled={false}
                            value={formData?.dateFilter ? new Date(formData.dateFilter) : null}
                            withMask
                            onDateChange={value => onUpdateDateFields(value)}
                            data-testid="datepicker-dateFilter"
                        />
                    </StyledField>

                    <StyledButton data-testid="form-buttons-wrapper">
                        <GIButton
                            id="submit_btn"
                            labelId="screenpop.button.go"
                            isDisabled={false}
                            type="submit"
                            data-testid="submit-button"
                        />
                        <GIButton
                            id="reset_btn"
                            labelId="button.reset"
                            isDisabled={false}
                            type="reset"
                            variant="secondary"
                            onClickHandler={e => handleReset(e)}
                            data-testid="reset-button"
                        />
                    </StyledButton>
                </StyledGrid>
            </form>

            <PaginatedTable
                data={content}
                columns={columns}
                pageSize={currentPageSize}
                currentPage={currentPage}
                totalPages={totalPages}
                totalItems={totalElements}
                onPageChange={handlePageChange}
                onPageSizeChange={handlePageSizeChange}
                startPageIndex={first ? pageNumber : 0}
                hidePageControl={false}
            />

            <ConditionalRendering isTrue={showModal}>
                <AdminSerffModal
                    modalTitleId={modalData?.title || ''}
                    closeModal={() => setShowModal(false)}
                    content={
                        <HandleModalContent
                            modaltype={modalData?.type}
                            modalContent={modalData?.content || ''}
                            handleModalContent={handleModalContent}
                        />
                    }
                    data-testid="serff-transfer-status-modal"
                />
            </ConditionalRendering>
        </StyledContainer>
    );    
};

export default AdminSerffTransferStatusContainer;
